import { DashboardCard } from "@dashboard/components/Card";
import { WsmErrorFragment, WsmKitRuleKind } from "@dashboard/graphql";
import { getWsmIndexedErrors,getWsmKitsErrorMessage } from "@dashboard/wsmKits/errors";
import { WsmKitMemberDraft, WsmKitRuleDraft } from "@dashboard/wsmKits/types";
import { getRuleTargetChoices, kitCanHaveRules } from "@dashboard/wsmKits/utils";
import { Box, Button, Checkbox, Input, Select, Text, TrashBinIcon } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { messages } from "./messages";

interface WsmKitRulesCardProps {
  members: WsmKitMemberDraft[];
  rules: WsmKitRuleDraft[];
  errors: WsmErrorFragment[];
  disabled: boolean;
  onAdd: () => void;
  onRemove: (key: string) => void;
  onChange: (key: string, change: Partial<WsmKitRuleDraft>) => void;
}

export const WsmKitRulesCard = ({
  members,
  rules,
  errors,
  disabled,
  onAdd,
  onRemove,
  onChange,
}: WsmKitRulesCardProps) => {
  const intl = useIntl();
  const rowErrors = getWsmIndexedErrors(errors, "rules");
  const kindChoices = [
    { value: WsmKitRuleKind.REQUIRES_ONE_OF, label: intl.formatMessage(messages.requiresOneOf) },
    { value: WsmKitRuleKind.EXCLUDES, label: intl.formatMessage(messages.excludes) },
  ];
  const subjectChoices = members.map(member => ({
    value: member.key,
    label: member.sku ? `${member.productName} (${member.sku})` : member.productName,
  }));

  return (
    <DashboardCard data-test-id="wsm-kit-rules">
      <DashboardCard.Header>
        <DashboardCard.Title>{intl.formatMessage(messages.title)}</DashboardCard.Title>
        <DashboardCard.Toolbar>
          <Button
            variant="secondary"
            disabled={disabled || !kitCanHaveRules(members.length)}
            onClick={onAdd}
            data-test-id="wsm-kit-add-rule"
          >
            {intl.formatMessage(messages.addRule)}
          </Button>
        </DashboardCard.Toolbar>
      </DashboardCard.Header>
      <DashboardCard.Content>
        {rules.length === 0 && (
          <Text size={3} color="default2" data-test-id="wsm-kit-rules-empty">
            {/* The same predicate gates the Add rule button, so a kit that
                already has members is never told to add members first. */}
            {intl.formatMessage(
              kitCanHaveRules(members.length) ? messages.noRulesYet : messages.addMembersFirst,
            )}
          </Text>
        )}
        {rules.map((rule, index) => {
          const error = rowErrors[index];
          const errorMessage = getWsmKitsErrorMessage(error, intl);
          // The one place the scoping lives: a target choice is a member of THIS
          // kit and never the subject itself. The server refuses anything else
          // with RULE_TARGET_NOT_IN_KIT.
          const targetChoices = getRuleTargetChoices(members, rule.subjectKey);

          return (
            <Box
              key={rule.key}
              borderStyle="solid"
              borderWidth={1}
              borderColor="default1"
              borderRadius={3}
              padding={4}
              marginBottom={4}
              display="grid"
              gap={4}
              data-test-id={`wsm-kit-rule-${index}`}
            >
              {/* A grid child defaults to min-content width, which is what let a
                  long product name push the Select past the card at 1024. */}
              <Box display="grid" __gridTemplateColumns="minmax(0, 1fr) minmax(0, 1fr)" gap={4}>
                <Select
                  label={intl.formatMessage(messages.subject)}
                  data-test-id={`wsm-kit-rule-subject-${index}`}
                  disabled={disabled}
                  options={subjectChoices}
                  value={rule.subjectKey}
                  onChange={value =>
                    onChange(rule.key, {
                      subjectKey: value as string,
                      // A subject can never be its own target.
                      targetKeys: rule.targetKeys.filter(key => key !== value),
                    })
                  }
                />
                <Select
                  label={intl.formatMessage(messages.kind)}
                  data-test-id={`wsm-kit-rule-kind-${index}`}
                  disabled={disabled}
                  options={kindChoices}
                  value={rule.kind}
                  onChange={value => onChange(rule.key, { kind: value as WsmKitRuleKind })}
                />
              </Box>
              <Box>
                <Text size={2} color="default2">
                  {intl.formatMessage(messages.targets)}
                </Text>
                {/* A grid column is max-content by default and a grid item keeps
                    min-width:auto, so a supplier title long enough to matter used
                    to push the whole tick list past the card. The track is pinned
                    and every item is allowed to shrink into it. */}
                <Box
                  display="grid"
                  __gridTemplateColumns="minmax(0, 1fr)"
                  gap={1}
                  paddingTop={2}
                >
                  {targetChoices.map(choice => (
                    <Checkbox
                      key={choice.value}
                      __minWidth="0"
                      disabled={disabled}
                      name={`rule-${index}-target`}
                      data-test-id={`wsm-kit-rule-target-${index}-${choice.value}`}
                      checked={rule.targetKeys.includes(choice.value)}
                      onCheckedChange={checked =>
                        onChange(rule.key, {
                          targetKeys: checked
                            ? [...rule.targetKeys, choice.value]
                            : rule.targetKeys.filter(key => key !== choice.value),
                        })
                      }
                    >
                      <Text
                        size={3}
                        display="block"
                        __minWidth="0"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        __whiteSpace="nowrap"
                        title={choice.label}
                      >
                        {choice.label}
                      </Text>
                    </Checkbox>
                  ))}
                </Box>
              </Box>
              <Input
                label={intl.formatMessage(messages.message)}
                data-test-id={`wsm-kit-rule-message-${index}`}
                disabled={disabled}
                value={rule.message}
                onChange={event => onChange(rule.key, { message: event.target.value })}
                error={!!error}
                helperText={errorMessage}
              />
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="secondary"
                  icon={<TrashBinIcon />}
                  disabled={disabled}
                  onClick={() => onRemove(rule.key)}
                  data-test-id={`wsm-kit-rule-remove-${index}`}
                  aria-label={intl.formatMessage(messages.removeRule)}
                />
              </Box>
            </Box>
          );
        })}
      </DashboardCard.Content>
    </DashboardCard>
  );
};

export default WsmKitRulesCard;
